// src/features/task/TaskChecklists.tsx
// Checklists on a task: always rendered (with an empty state), toggle
// completion (ToggleChecklistItem), edit an item's text (UpdateChecklistItem),
// and add a brand-new checklist with its initial items (AddChecklist). There
// is no delete-item / delete-checklist RPC on TaskService, so this panel does
// not offer either — surfacing a delete affordance that silently no-ops would
// be worse than omitting it.

import { useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Button,
  Alert,
} from '@react/components';
import { TaskSection } from '@/features/task/detail/TaskSection';
import { useTaskSection } from '@/features/task/detail/sections';
import { getDisplayName } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';

interface TaskChecklistsProps {
  taskId: string;
  checklists: any[];
  editable?: boolean;
  onChanged?: () => void;
}

interface DraftItem {
  id: string;
  content: string;
}

export const TaskChecklists = observer(({ taskId, checklists, editable = true, onChanged }: TaskChecklistsProps) => {
  const store = useTask();
  const { t } = useI18n();

  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  // New-checklist composer state.
  const [composerOpen, setComposerOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newItems, setNewItems] = useState<DraftItem[]>([]);
  const [newItemDraft, setNewItemDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const list = Array.isArray(checklists) ? checklists : ((checklists as any)?.checklists ?? []);

  const toggle = async (checklistId: string, itemId: string, isDone: boolean) => {
    setError(null);
    try {
      await store.ToggleChecklistItem(taskId, checklistId, itemId, { is_done: !isDone } as any);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.checklistPanel.toggleError')));
    }
  };

  const startEdit = (itemId: string, content: string) => {
    setEditing(itemId);
    setDraft(content || '');
  };

  const saveEdit = async (checklistId: string, itemId: string) => {
    setBusy(true);
    setError(null);
    try {
      await store.UpdateChecklistItem(taskId, checklistId, itemId, {
        item: { content: draft.trim() },
      } as any);
      setEditing(null);
      setDraft('');
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.checklistPanel.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const addDraftItem = () => {
    const content = newItemDraft.trim();
    if (!content) return;
    setNewItems((prev) => [...prev, { id: crypto.randomUUID(), content }]);
    setNewItemDraft('');
  };

  const removeDraftItem = (id: string) => {
    setNewItems((prev) => prev.filter((it) => it.id !== id));
  };

  const resetComposer = () => {
    setComposerOpen(false);
    setNewTitle('');
    setNewItems([]);
    setNewItemDraft('');
  };

  const saveNewChecklist = async () => {
    const title = newTitle.trim();
    if (!title && newItems.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await store.AddChecklist(taskId, {
        task: { id: taskId },
        checklist: {
          title: title || t('task.checklist'),
          items: {
            items: newItems.map((it, i) => ({ content: it.content, is_done: false, order: i })),
          },
        },
      } as any);
      resetComposer();
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.checklistPanel.addChecklistError')));
    } finally {
      setSaving(false);
    }
  };

  // An empty checklist section is not drawn at all — the page offers it instead.
  const visible = useTaskSection('checklists', list.length > 0);
  if (!visible && !composerOpen) return null;

  return (
    <TaskSection
      title={t('task.checklists')}
      action={
        editable && !composerOpen ? (
          <Button size="sm" variant="ghost" onClick={() => setComposerOpen(true)}>
            {t('task.addChecklist')}
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {list.map((checklist: any) => {
          // A checklist with no items can arrive with `items` as the bare
          // wrapper `{}` — truthy, but not an array. Guard before .filter.
          const rawItems = checklist.items?.items ?? checklist.items;
          const items = Array.isArray(rawItems) ? rawItems : [];
          const done = items.filter((ci: any) => ci.is_done).length;
          const total = items.length;
          const progress = total > 0 ? Math.round((done / total) * 100) : 0;
          return (
            <div key={checklist.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-foreground">{checklist.title}</h4>
                <span className="text-xs text-muted-foreground">{done}/{total}</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="space-y-0.5">
                {items.map((ci: any) => (
                  <div key={ci.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted transition-colors">
                    <input
                      type="checkbox"
                      aria-label={ci.content}
                      checked={ci.is_done}
                      onChange={() => toggle(checklist.id, ci.id, ci.is_done)}
                      className="rounded border-input"
                    />
                    {editing === ci.id ? (
                      <input
                        type="text"
                        aria-label={t('task.checklistPanel.editItem')}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(checklist.id, ci.id); if (e.key === 'Escape') setEditing(null); }}
                        onBlur={() => saveEdit(checklist.id, ci.id)}
                        disabled={busy}
                        className="flex-1 rounded-md border border-input bg-background px-2 py-0.5 text-sm text-foreground"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => editable && startEdit(ci.id, ci.content)}
                        className={`flex-1 text-left text-sm ${ci.is_done ? 'line-through text-muted-foreground' : 'text-foreground'} ${editable ? 'cursor-text' : ''}`}
                      >
                        {ci.content}
                      </button>
                    )}
                    {ci.assignee && (
                      <span className="text-xs text-muted-foreground ml-auto shrink-0">{getDisplayName(ci.assignee)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {composerOpen && (
          <div className="space-y-2 rounded-md border border-border p-3">
            <input
              type="text"
              aria-label={t('task.checklistTitle')}
              placeholder={t('task.checklistTitle')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
            {newItems.length > 0 && (
              <div className="space-y-1">
                {newItems.map((it) => (
                  <div key={it.id} className="flex items-center gap-2 group">
                    <span className="flex-1 text-sm text-foreground">{it.content}</span>
                    <button
                      type="button"
                      onClick={() => removeDraftItem(it.id)}
                      className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"
                      aria-label={t('common.remove')}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-1.5">
              <input
                type="text"
                aria-label={t('task.addChecklistItem')}
                placeholder={t('task.checklistItemPlaceholder')}
                value={newItemDraft}
                onChange={(e) => setNewItemDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraftItem(); } }}
                className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
              <Button size="sm" variant="ghost" onClick={addDraftItem} disabled={!newItemDraft.trim()}>
                {t('task.addChecklistItem')}
              </Button>
            </div>
            <div className="flex gap-1.5">
              <Button
                size="sm"
                onClick={saveNewChecklist}
                isLoading={saving}
                disabled={!newTitle.trim() && newItems.length === 0}
              >
                {t('common.save')}
              </Button>
              <Button size="sm" variant="ghost" disabled={saving} onClick={resetComposer}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </TaskSection>
  );
});

export default TaskChecklists;
